import { RadioGroup } from '@/components/RadioGroup';
import {
    EIncomeSource,
    EOtherIncomeSource,
    selectEmploymentStatusByIndex,
    selectOtherIncomeSource,
    setOtherIncomeSource,
} from '@/context/borrower';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IOtherIncomeSource {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'OtherIncomeSource';

export function OtherIncomeSource({
    baseId,
    showErrors,
}: IOtherIncomeSource): JSX.Element {
    // ***** Redux *****
    const otherIncomeSource = useAppSelector(selectOtherIncomeSource);
    const primaryIncomeSource = useAppSelector(
        (state: RootState) =>
            selectEmploymentStatusByIndex(state, 0)?.incomeSource
    );
    const dispatch = useAppDispatch();

    // ***** Event Handlers *****
    function handleChange(value: string | boolean | undefined): void {
        dispatch(setOtherIncomeSource(value as EOtherIncomeSource));
    }

    // ***** Render *****
    /*
     * Government benefits is offered here only when it is not already the
     * answer above. Listing it twice on one screen invites the same payment
     * being declared as both the primary income and the top-up, which lands
     * in Manage as two amounts and is counted twice in the totals.
     *
     * Someone on two different payments still has Other, which reads more
     * honestly in the record than a second benefits line would.
     */
    const allOptions = [
        {
            label: 'Rental / Investment',
            value: EOtherIncomeSource.RENTAL_OR_INVESMENT,
            id: `${baseId}-button-otherIncomeSource-rentalOrInvestment`,
        },
        {
            label: 'Government Benefits',
            value: EOtherIncomeSource.GOVERNMENT_BENEFITS,
            id: `${baseId}-button-otherIncomeSource-governmentBenefits`,
        },
        {
            label: 'Other',
            value: EOtherIncomeSource.OTHER,
            id: `${baseId}-button-otherIncomeSource-other`,
        },
    ];

    const options =
        primaryIncomeSource === EIncomeSource.GOVERNMENT_PAYMENT
            ? allOptions.filter(
                  (option) =>
                      option.value !== EOtherIncomeSource.GOVERNMENT_BENEFITS
              )
            : allOptions;

    return (
        <div data-testid={testId}>
            <RadioGroup
                // RadioGroup lays its rows out once on mount, so a changed
                // option count needs a remount to be picked up.
                key={options.length}
                title='Source of other income'
                showErrors={showErrors && otherIncomeSource === undefined}
                onChange={handleChange}
                options={options}
                value={otherIncomeSource}
                tooltipContent='If this extra income comes from more than one place, pick the largest. Choose Other if it is not listed.'
            />
        </div>
    );
}
